import apiService from '../../../services/api'

/**
 * Battle API Service
 * Handles all battle-related REST API endpoints
 */

export const battleApi = {
  /**
   * Получить список всех активных баттлов и доступных кейсов
   * GET /api/casebattle/list/
   * Возвращает:
   * - battles: объект с ID баттлов как ключи (нужно конвертировать в массив)
   * - cases: массив доступных кейсов для создания баттла
   */
  getBattles: async () => {
    try {
      const response = await apiService.makeRequest('/api/casebattle/list/', {
        method: 'GET'
      })
      
      // Конвертируем battles из объекта в массив и добавляем ID к каждому баттлу
      const battlesObject = response.battles || {}
      const battlesArray = Object.entries(battlesObject).map(([id, battle]) => ({
        id, // Добавляем ID баттла
        ...battle
      }))
      
      return {
        battles: battlesArray,
        cases: response.cases || [],
        categoryOrder: response.category_order || [],
        games: response.games || []
      }
    } catch (error) {
      console.error('Get battles error:', error)
      throw error
    }
  },

  /**
   * Получить информацию о конкретном баттле
   * GET /api/battle/:id
   */
  getBattle: async (battleId) => {
    try {
      const response = await apiService.makeRequest(`/api/battle/${battleId}`, {
        method: 'GET'
      })
      return response.battle
    } catch (error) {
      console.error('Get battle error:', error)
      throw error
    }
  },

  /**
   * Создать новый баттл
   * POST /api/casebattle/create/
   * Body: { 
   *   case_id: string, 
   *   count_players: number (2-4), 
   *   count_case: number (1-5), 
   *   type: string ("more" | "less" | "random")
   * }
   * Возвращает: { create_casebattle: {...}, me: {...} }
   */
  createBattle: async ({ caseId, countPlayers, countCase, type = 'more' }) => {
    try {
      const response = await apiService.makeRequest('/api/casebattle/create/', {
        method: 'POST',
        body: JSON.stringify({ 
          case_id: caseId,
          count_players: countPlayers,
          count_case: countCase,
          type: type
        })
      })
      
      // Проверяем на ошибки в ответе
      if (response.error) {
        const error = new Error(response.error)
        error.message = response.error
        throw error
      }
      
      // Возвращаем данные созданного баттла и обновленного пользователя
      return {
        battle: response.create_casebattle,
        user: response.me
      }
    } catch (error) {
      console.error('Create battle error:', error)
      throw error
    }
  },

  /**
   * Присоединиться к баттлу
   * GET /api/casebattle/join/:id/
   * Возвращает:
   * - join_casebattle: данные о присоединении (id, case, status, slot, need_balance и т.д.)
   * - result_casebattle: массив результатов розыгрыша (если присоединился последний игрок)
   * - me: обновленные данные пользователя (баланс)
   */
  joinBattle: async (battleId) => {
    try {
      const response = await apiService.makeRequest(`/api/casebattle/join/${battleId}/`, {
        method: 'GET'
      })
      
      // Проверяем на ошибки в ответе
      if (response.error) {
        const error = new Error(response.error)
        error.message = response.error
        throw error
      }
      
      // Возвращаем все данные из ответа
      return {
        battle: response.join_casebattle,
        results: response.result_casebattle || null, // Может быть null если не последний игрок
        user: response.me
      }
    } catch (error) {
      console.error('Join battle error:', error)
      throw error
    }
  },

  /**
   * Получить историю/текущее состояние конкретного баттла
   * GET /api/casebattle/history/:id/
   * Используется для polling обновлений баттла каждую секунду
   * Возвращает:
   * - battles: объект с данными баттла (case, status, players, result_casebattle и т.д.)
   */
  getBattleHistory: async (battleId) => {
    try {
      const response = await apiService.makeRequest(`/api/casebattle/history/${battleId}/`, {
        method: 'GET'
      })
      
      // Проверяем на ошибки в ответе
      if (response.error) {
        const error = new Error(response.error)
        error.message = response.error
        throw error
      }
      
      // Возвращаем данные баттла с добавлением ID
      return {
        ...response.battles,
        id: battleId
      }
    } catch (error) {
      console.error('Get battle history error:', error)
      throw error
    }
  },

  /**
   * Получить историю всех завершенных баттлов (последние 100)
   * GET /api/casebattle/history/
   * Возвращает:
   * - battles: объект с ID баттлов как ключи
   * Фильтрует баттлы с неполными данными (null players)
   */
  getAllBattlesHistory: async () => {
    try {
      const response = await apiService.makeRequest('/api/casebattle/history/', {
        method: 'GET'
      })
      
      // Проверяем на ошибки в ответе
      if (response.error) {
        const error = new Error(response.error)
        error.message = response.error
        throw error
      }
      
      // Конвертируем battles из объекта в массив и добавляем ID к каждому баттлу
      const battlesObject = response.battles || {}
      const battlesArray = Object.entries(battlesObject)
        .map(([id, battle]) => ({
          id,
          ...battle
        }))
        // Фильтруем баттлы с неполными данными (где первый игрок null)
        .filter(battle => {
          const hasValidPlayers = battle.players && 
            battle.players.length > 0 && 
            battle.players[0].user_id !== null
          return hasValidPlayers
        })
      
      return battlesArray
    } catch (error) {
      console.error('Get all battles history error:', error)
      throw error
    }
  },

  /**
   * Получить историю моих баттлов (последние 100)
   * GET /api/casebattle/history/me/
   * Возвращает:
   * - battles: объект с ID баттлов как ключи (только баттлы где you: 1)
   * Фильтрует баттлы с неполными данными (null players)
   */
  getMyBattlesHistory: async () => {
    try {
      const response = await apiService.makeRequest('/api/casebattle/history/me/', {
        method: 'GET'
      })
      
      // Проверяем на ошибки в ответе
      if (response.error) {
        const error = new Error(response.error)
        error.message = response.error
        throw error
      }
      
      // Конвертируем battles из объекта в массив и добавляем ID к каждому баттлу
      const battlesObject = response.battles || {}
      const battlesArray = Object.entries(battlesObject)
        .map(([id, battle]) => ({
          id,
          ...battle
        }))
        // Фильтруем баттлы с неполными данными (где первый игрок null)
        .filter(battle => {
          const hasValidPlayers = battle.players && 
            battle.players.length > 0 && 
            battle.players[0].user_id !== null
          return hasValidPlayers
        })
      
      return battlesArray
    } catch (error) {
      console.error('Get my battles history error:', error)
      throw error
    }
  }
}

export default battleApi
